import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useInlineEditorBehavior } from '../../hooks/useInlineEditorBehavior'

interface InlineDateEditorProps {
  /** Current value (ISO date string or null) */
  value: string | null | undefined
  /** Field name for display/debugging purposes */
  fieldName: string
  /** Called when user saves a new value */
  onSave: (newValue: string | null) => Promise<void>
  /** Called when user cancels editing */
  onCancel: () => void
}

/**
 * Inline editor for date fields in market tables.
 * - Uses HTML5 date input
 * - Auto-saves on blur or Enter key
 * - Cancels on Escape key
 */
export default function InlineDateEditor({
  value,
  fieldName: _fieldName,
  onSave,
  onCancel
}: InlineDateEditorProps) {
  // Convert ISO date string to YYYY-MM-DD format for date input
  const toDateInputValue = (isoDate: string | null | undefined): string => {
    if (!isoDate) return ''
    try {
      const date = new Date(isoDate)
      if (isNaN(date.getTime())) return ''
      return date.toISOString().split('T')[0]
    } catch {
      return ''
    }
  }

  const [inputValue, setInputValue] = useState(toDateInputValue(value))
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const handleSave = useCallback(async () => {
    if (isSaving) return

    // Parse the value - convert to ISO string or null
    const newValue = inputValue.trim() === '' ? null : inputValue

    // Check if value actually changed
    const oldDateValue = toDateInputValue(value)
    if (newValue === oldDateValue || (newValue === null && oldDateValue === '')) {
      onCancel()
      return
    }

    setIsSaving(true)
    try {
      await onSave(newValue)
    } catch (error) {
      setIsSaving(false)
    }
  }, [inputValue, value, onSave, onCancel, isSaving])

  const { handleKeyDown, handleBlur } = useInlineEditorBehavior({
    onSave: handleSave,
    onCancel,
    containerRef
  })

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setInputValue('')
  }, [])

  return (
    <div
      ref={containerRef}
      className="absolute z-50 bg-white shadow-lg rounded p-1 -ml-1 -mt-1"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="date"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={isSaving}
          className={`
            w-32 px-2 py-1 text-sm text-center
            border border-green-500 rounded
            focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
            ${isSaving ? 'bg-gray-100 text-gray-500' : 'bg-white'}
          `}
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            onMouseDown={(e) => e.preventDefault()} // Prevent blur
            className="text-xs text-red-600 hover:text-red-800 px-1"
            title="Clear date"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  )
}

